import { Campaign } from '@/types/campaign.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import CampaignsTableHeader from './campaigns-table-header';
import CampaignsTableRow from './campaigns-table-row';
import useComputedColorVariableValue from '@/utils/colors/use-computed-color-variable-value';
import Paging from '@/general-ui/paging/paging';
import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import CustomReactSelect from '@ui/select/react-select/custom-react-select';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import Text from '@ui/text/text/text';
import { useMemo } from 'react';

interface CampaignsTableProps {
  campaigns: Campaign[];
  onEdit: (id: string) => void;
  selectedIds: string[];
  onSelect: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  deleteSelected: () => void;
}

const ROWS_PER_PAGE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
];

const styleMap = reactSelectAdminStyles({
  control: () => ({
    padding: 0,
    paddingLeft: 0,
    border: '1px solid var(--gray-300)',
    margin: 0,
    minHeight: '32px',
    width: '80px',
  }),
  placeholder: () => ({ fontSize: '12px', fontWeight: 500 }),
  menuList: () => ({
    width: '80px',
  }),
  option: () => ({
    display: 'flex',
    cursor: 'pointer',
    fontSize: '12px',
  }),
  singleValue: () => ({
    fontSize: '12px',
    fontWeight: 600,
  }),
});

const CampaignsTable = ({
  campaigns,
  onEdit,
  selectedIds,
  onSelect,
  onSelectAll,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  deleteSelected,
}: CampaignsTableProps) => {
  const computedBgHex = useComputedColorVariableValue({ variable: 'var(--BG)' });
  const selectableLength = useMemo(() => {
    return campaigns.filter((c) => c.state !== 'sent').length;
  }, [campaigns]);

  // Pagination Logic
  const totalPages = Math.ceil(campaigns.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedCampaigns = campaigns.slice(startIndex, startIndex + rowsPerPage);

  // Selection Logic
  const allSelected = selectableLength > 0 && selectedIds.length === selectableLength;
  const partialSelected = selectedIds.length > 0 && selectedIds.length < selectableLength;

  const handleSelectAllClick = () => {
    if (allSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(campaigns.filter((c) => c.state !== 'sent').map((c) => c.uuid));
    }
  };

  return (
    <div className="flex-column gap-16">
      <Table>
        <TableHead border={0} bgcolor={computedBgHex} className="position-sticky top-0 z-2 height-48">
          <TableRow border={0} className="height-30">
            <CampaignsTableHeader
              allSelected={allSelected}
              partialSelected={partialSelected}
              onSelectAll={handleSelectAllClick}
              selectedCount={selectedIds.length}
              onClearSelection={() => onSelectAll([])}
              deleteSelected={deleteSelected}
            />
          </TableRow>
        </TableHead>
        <tbody aria-hidden="true">
          <tr>
            <td style={{ height: '8px', padding: 0 }} />
          </tr>
        </tbody>
        <TableBody>
          {paginatedCampaigns.map((campaign) => (
            <CampaignsTableRow
              key={campaign.uuid}
              campaign={campaign}
              onEdit={onEdit}
              selected={selectedIds.includes(campaign.uuid)}
              onSelect={onSelect}
            />
          ))}
        </TableBody>
      </Table>

      <Container className="flex ai-center jc-space-between padding-v-24">
        <div style={{ width: '200px' }}></div> {/* Spacer for centering */}
        <OptionalComponent display={totalPages > 1}>
          <Paging page={page} setPage={onPageChange} totalPages={totalPages} />
        </OptionalComponent>
        <div className="flex ai-center gap-8" style={{ width: '200px', justifyContent: 'flex-end' }}>
          <Text className="P2" color="var(--gray-500)">
            Rows per page:
          </Text>
          <CustomReactSelect
            options={ROWS_PER_PAGE_OPTIONS}
            value={ROWS_PER_PAGE_OPTIONS.find((o) => o.value === rowsPerPage)}
            onChange={(option: unknown) => {
              const selected = option as { label: string; value: number } | null;
              if (selected) {
                onRowsPerPageChange(selected.value);
                onPageChange(1); // Reset to page 1 on scale change
              }
            }}
            styles={styleMap}
            isSearchable={false}
          />
        </div>
      </Container>
    </div>
  );
};

export default CampaignsTable;
